import classNames from './RecordingPanelView.module.css';
import ccss from "../../Styles/common.module.css";
import { observer } from 'mobx-react-lite';
import { JsonOverlayView } from './JsonOverlayView';
import { PrimaryTabIds, PrimaryTabLabels, RecordingPanelState } from './RecordingPanelState';
import { ChronologicalEventsSubpanelControlsView } from './EventsSubpanelControlsView';
import { ChronologicalSearchResultsView } from './ChronologicalSearchResultsView';
import { SearchReportBySearchTermView } from './Reports/SearchReportBySearchTermView';
import { DataRecipientsReportView } from './Reports/DataRecipientsReportView';
import { RecordingPanelSideBarView } from './RecordingPanelSideBarView';
import { CompletedRecordingMenu } from './CompletedRecordingMenu';
import { TrackingCodeReportView } from './Reports/TrackingCodeReportView';
import { isRunningInExtensionsDevToolsPanel } from '../../Pages/devtools/isProcessRunningInExtensionPage';
import { saveHarFromWithinPanel } from './saveHarFromWithinPanel';
import { IpReportView } from './Reports/IpReportView';
import { SearchDetailsDeferRenderUntilOpenedContext } from '../../Styles/ReportStyles';
import { DevToolsPanelState } from '../../Pages/DevToolsPanelState';
import { UnlicensedTextView } from "../../constants/UnlicensedTextView";
import { AnalyticsReportFromDatabaseView } from './Reports/AnalyticsTrafficView';

const LicensedHeaderView = observer ( ({recordingPanelState}: {recordingPanelState: RecordingPanelState})  => {
	const {licensePermissions, sourcePageDomains} = recordingPanelState;
	const licensedAtAllDomains = sourcePageDomains.every( pageDomain => licensePermissions.mayUseForCommercialPurposesAt({pageDomain}) );
	if (licensedAtAllDomains) return null;
	return (
		<div className={classNames.LicenseHeaderFixedInPlace}>
			<div className={classNames.LicenseHeader}><UnlicensedTextView/></div>
		</div>
	)
});


export const RecordingPanelViewRouter = observer ( ({recordingPanelState}: {recordingPanelState: RecordingPanelState}) => {
	const {searchUiState, searchResultsObserver, objectToDisplayAsJson, requestGetter,
		eventsTableAnalysisResults, causationGraphObserver,
		getsDbEventsByIndex,
		 filteredChronologicalResultsObserver, setObjectToDisplayAsJson,
		 recording,
		 recordingUuid: _,
		 licenseCache,
		 callStackResolution,
	} = recordingPanelState;
	if (recording == null) return null;
	const licensePermissions = licenseCache;
	// ProcessInfo.log(`recordingPanelState.recording`, recording.externallyVisibleIpAddress, toJS(recording))
	const ipAddress = recordingPanelState.recording?.externallyVisibleIpAddress;
	return (<SearchDetailsDeferRenderUntilOpenedContext value={true}>
		<div className={classNames.RecordingPanelContainerElement}>{
			recordingPanelState.primaryTabId === 'Chronological' ? (
				eventsTableAnalysisResults.originAndDestinationUrlsObserver.isEmpty ? (
						<div className={classNames.ScrollablePanelContentContainerElement}>loading...</div>
				) : (
					<>
						<ChronologicalEventsSubpanelControlsView {...{recordingPanelState}} />
						<div className={classNames.ScrollablePanelContentContainerElement}>
							<LicensedHeaderView {...{recordingPanelState}} />
							<ChronologicalSearchResultsView  {...{callStackResolution, licensePermissions, chronologicalResultsObserver: filteredChronologicalResultsObserver, setObjectToDisplayAsJson, requestGetter}} startTimeIso={recording.startTimeIso} />
						</div>
					</>
				)
			) : recordingPanelState.primaryTabId === 'IP' ? (
					<div className={classNames.ScrollablePanelContentContainerElement}>
						<LicensedHeaderView {...{recordingPanelState}} />
						<IpReportView {...{licensePermissions, callStackResolution, requestGetter, getsDbEventsByIndex, searchUiState, causationGraphObserver, searchResultsObserver, setObjectToDisplayAsJson, ipAddress}} />
					</div>
			) : recordingPanelState.primaryTabId === 'Search' ? (
					<div className={classNames.ScrollablePanelContentContainerElement}>
						<LicensedHeaderView {...{recordingPanelState}} />
						<SearchReportBySearchTermView {...{licensePermissions, callStackResolution, requestGetter, getsDbEventsByIndex, searchUiState, causationGraphObserver, searchResultsObserver, setObjectToDisplayAsJson}} />
					</div>
			) : recordingPanelState.primaryTabId === 'DataRecipients' ? (
					<div className={classNames.ScrollablePanelContentContainerElement}>
						<LicensedHeaderView {...{recordingPanelState}} />
						<DataRecipientsReportView {...{...eventsTableAnalysisResults, licensePermissions, callStackResolution, getsDbEventsByIndex, requestGetter, setObjectToDisplayAsJson}} />
					</div>
			): recordingPanelState.primaryTabId === 'TrackingCode' ? (
					<div className={classNames.ScrollablePanelContentContainerElement}>
						<LicensedHeaderView {...{recordingPanelState}} />
						<TrackingCodeReportView {...{...eventsTableAnalysisResults, licensePermissions, callStackResolution, getsDbEventsByIndex, setObjectToDisplayAsJson, requestGetter}} />
					</div>
			): recordingPanelState.primaryTabId === "Analytics" ? (
					<div className={classNames.ScrollablePanelContentContainerElement}>
						<LicensedHeaderView {...{recordingPanelState}} />
						<AnalyticsReportFromDatabaseView {...{recordingUuid: recordingPanelState.recordingUuid, licensePermissions}} />
					</div>
			): null 
			}
			{ recordingPanelState.objectToDisplayAsJson == null ? null : (
				<JsonOverlayView {...{objectToDisplayAsJson, setObjectToDisplayAsJson}} />
			)}
		</div>
	</SearchDetailsDeferRenderUntilOpenedContext>);
});

export const ActiveRecordingButtons = observer ( ({recordingPanelState}: {recordingPanelState: RecordingPanelState}) => {
	return (
		<div className={ccss.RightAlignContents}>
			<div className={ccss.ToolTipContainer}>
				<span className={ccss.ToolTipLeft}>Save HAR data into evidence for this recording</span>
				<button className={ccss.borderless} onClick={ () => saveHarFromWithinPanel(recordingPanelState.recordingUuid)}>⤓</button>
			</div>
		</div>
	);
});

export const RecordingPanelTabHeaderView = observer ( ({recordingPanelState}: {recordingPanelState: RecordingPanelState}) => {
	const tabIds = recordingPanelState.mayGenerateIpReports ? PrimaryTabIds : PrimaryTabIds.filter( x => x !== 'IP');
	return (
		<div className={classNames.RecordingPanelTabHeaderElement}>
			{recordingPanelState.recording?.videoRecordingInProgress ? (
					<span>🔴</span>
				) : null
			}
			{tabIds.map( (primaryTabId) => (
				<div className={[
						classNames.RecordingPanelTabItemElement,
						...(recordingPanelState.primaryTabId === primaryTabId ? ['selected'] : [])
						].join(" ")}
					key={primaryTabId}
					onClick={ () => recordingPanelState.setPrimaryTab(primaryTabId)}
				>{
					PrimaryTabLabels[primaryTabId]
				}</div>
			))}
			{ isRunningInExtensionsDevToolsPanel ? 
				<ActiveRecordingButtons recordingPanelState={recordingPanelState} /> :
				<CompletedRecordingMenu recordingPanelState={recordingPanelState} />			
			}

		</div>
	)
});

export const RecordingPanelView = observer(({ recordingPanelState, devToolsPanelState }: {
	recordingPanelState: RecordingPanelState;
	devToolsPanelState?: DevToolsPanelState;
}) => {
	return (
		<div className={classNames.PanelContainerElement}>
			<div className={classNames.PanelContentContainerElement}>
				<RecordingPanelTabHeaderView {...{recordingPanelState}} />
				<RecordingPanelViewRouter {...{ recordingPanelState }} />
			</div>
			<RecordingPanelSideBarView {...{ recordingPanelState, devToolsPanelState }} />
		</div>
	);
});
